/**
 * CustomerDetailPage — Phase 34.10.
 *
 * Shows customer profile, onboarding status, open tickets, usage snapshot,
 * churn risk, and recent feedback. All in one view.
 */

import { Navigate, Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { tierColor, healthColor, fmtTime, RISK_COLORS, displayTier } from './CustomersDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface CustomerSummary {
  id: string;
  email: string;
  tier: string;
  product_name: string;
  health_score: number | null;
  onboarded_at: number | null;
  deal_log_seq: number;
  created_at: number;
}

interface TicketSummary {
  id: string;
  customer_id: string;
  subject: string;
  body: string;
  language: string;
  status: string;
  response_body: string | null;
  chunks_used: number;
  created_at: number;
  resolved_at: number | null;
}

interface UsageSnapshot {
  customer_id: string;
  health_score: number | null;
  onboarded_at: number | null;
  open_ticket_count: number;
  resolved_ticket_count: number;
}

interface ChurnRisk {
  customer_id: string;
  risk_score: number;
  risk_tier: string;
  factors: unknown[];
  interventions: unknown[];
  escalated: boolean;
  computed_at: number;
}

// ── Data ─────────────────────────────────────────────────────────────────────

function useCustomer(id: string) {
  return useQuery({
    queryKey: ['customers', id],
    queryFn: () => apiFetch<CustomerSummary>(`/customers/${id}`),
    refetchInterval: 30_000,
  });
}

function useTickets(id: string) {
  return useQuery({
    queryKey: ['customers', id, 'tickets'],
    queryFn: () => apiFetch<{ tickets: TicketSummary[]; total: number }>(`/customers/${id}/tickets`),
    refetchInterval: 30_000,
  });
}

function useUsage(id: string) {
  return useQuery({
    queryKey: ['customers', id, 'usage'],
    queryFn: () => apiFetch<UsageSnapshot>(`/customers/${id}/usage`),
    refetchInterval: 30_000,
  });
}

function useChurnRisk(id: string) {
  return useQuery({
    queryKey: ['customers', id, 'churn-risk'],
    queryFn: () => apiFetch<ChurnRisk>(`/customers/${id}/churn-risk`),
    refetchInterval: 30_000,
    retry: false,
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10, borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.06))', paddingBottom: 4 }}>
        {title}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '4px 0' }}>
      <span style={{ width: 140, flexShrink: 0, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>{label}</span>
      <span style={{ fontSize: '9pt', color: color ?? 'var(--omega-ink)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function TicketRow({ t }: { t: TicketSummary }) {
  const statusColor = t.status === 'open' ? '#fbbf24' : t.status === 'resolved' ? '#34d399' : '#94a3b8';
  return (
    <Link
      to={`/omega/customers/support/${t.customer_id}/${t.id}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '8px 0',
        borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <span style={{ flexShrink: 0, fontSize: '7.5pt', padding: '1px 6px', border: `1px solid ${statusColor}`, color: statusColor, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3 }}>
        {t.status}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '9.5pt', color: 'var(--omega-ink)' }}>{t.subject || 'No subject'}</div>
        <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 2 }}>{fmtTime(t.created_at)}</div>
      </div>
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function CustomerDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { id = '' } = useParams<{ id: string }>();
  const { data: customer, isPending, isError } = useCustomer(id);
  const { data: ticketsData } = useTickets(id);
  const { data: usage } = useUsage(id);
  const { data: churnRisk } = useChurnRisk(id);

  if (isPending) {
    return (
      <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>
      </div>
    );
  }

  if (isError || !customer) {
    return (
      <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
        <div style={{ color: '#f87171', fontSize: '9pt' }}>Customer not found.</div>
      </div>
    );
  }

  const tickets = ticketsData?.tickets ?? [];
  const openTickets = tickets.filter(t => t.status === 'open');

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Customers', to: '/omega/customers' },
          { label: customer.email },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {customer.email}
        </h1>
        <span style={{ fontSize: '7.5pt', padding: '1px 8px', border: `1px solid ${tierColor(customer.tier)}`, color: tierColor(customer.tier), letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3 }}>
          {displayTier(customer.tier)}
        </span>
        {churnRisk && (
          <span style={{ fontSize: '7.5pt', padding: '1px 8px', border: `1px solid ${RISK_COLORS[churnRisk.risk_tier] ?? '#f87171'}`, color: RISK_COLORS[churnRisk.risk_tier] ?? '#f87171', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3 }}>
            {churnRisk.risk_tier} risk
          </span>
        )}
      </div>

      <Section title="Profile">
        <Field label="Email" value={customer.email} />
        <Field label="Product" value={customer.product_name || '—'} />
        <Field label="Tier" value={displayTier(customer.tier)} color={tierColor(customer.tier)} />
        <Field
          label="Health Score"
          value={customer.health_score !== null ? `${customer.health_score.toFixed(1)}` : '—'}
          color={healthColor(customer.health_score)}
        />
        <Field
          label="Onboarded"
          value={customer.onboarded_at ? fmtTime(customer.onboarded_at) : 'Not yet'}
          color={customer.onboarded_at ? '#34d399' : '#fbbf24'}
        />
        <Field label="Created" value={fmtTime(customer.created_at)} />
      </Section>

      {usage && (
        <Section title="Usage Snapshot">
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {[
              { label: 'Health Score', value: usage.health_score !== null ? `${usage.health_score.toFixed(1)}` : '—', color: healthColor(usage.health_score) },
              { label: 'Open Tickets', value: usage.open_ticket_count, color: usage.open_ticket_count > 0 ? '#fbbf24' : 'var(--omega-ink)' },
              { label: 'Resolved', value: usage.resolved_ticket_count, color: '#34d399' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: 'var(--omega-surface, rgba(255,255,255,0.04))', border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))', borderRadius: 6, padding: '12px 16px', minWidth: 100 }}>
                <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: '16pt', fontWeight: 700, color }}>{value}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {churnRisk && (
        <Section title="Churn Risk">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 14px', background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 6 }}>
            <div>
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginBottom: 2 }}>Risk Tier</div>
              <div style={{ fontSize: '11pt', fontWeight: 700, color: RISK_COLORS[churnRisk.risk_tier] ?? '#f87171', textTransform: 'uppercase' }}>{churnRisk.risk_tier}</div>
            </div>
            <div>
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginBottom: 2 }}>Score</div>
              <div style={{ fontSize: '11pt', fontWeight: 700, color: RISK_COLORS[churnRisk.risk_tier] ?? '#f87171' }}>{(churnRisk.risk_score * 100).toFixed(0)}%</div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)' }}>Computed {fmtTime(churnRisk.computed_at)}</div>
            </div>
          </div>
        </Section>
      )}

      <Section title={`Tickets${openTickets.length > 0 ? ` (${openTickets.length} open)` : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Link to={`/omega/customers/${id}/tickets`} style={{ fontSize: '8.5pt', color: '#7dd3fc', textDecoration: 'none' }}>
            All Tickets →
          </Link>
        </div>
        {tickets.length === 0 && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>No tickets.</div>}
        {tickets.slice(0, 5).map(t => <TicketRow key={t.id} t={t} />)}
      </Section>
    </div>
  );
}
